/**
 * Holidays — "Show holidays", the one setting the holiday layer has.
 *
 * ⚠️ WHY localStorage AND NOT `user_preferences`. Every pref in that table is
 * read through an EXPLICIT select list (`PREFS_SELECT_COLUMNS_LEGACY` in
 * `useUserPreferences.tsx`), so a new pref there is a new COLUMN — a schema
 * change, which is Kasandra-run SQL plus a migration under Track 3. The brief
 * for this feature said: if a toggle needs a column, skip it. localStorage
 * needs no column, so the toggle ships.
 *
 * The cost is honest and worth stating: this is PER DEVICE. Turning holidays
 * off on her phone leaves them on in a browser. For a display toggle on a
 * computed layer that is the right trade — nothing is lost by the two
 * disagreeing, because there is no data behind it. A pref that must follow her
 * between devices belongs in the table, with the migration that implies.
 *
 * ⚠️ Every access is wrapped. localStorage THROWS, not just returns null, in a
 * private window and under "block site data" — and this is called during
 * render, so an unguarded read would white-screen the Today tab rather than
 * lose a setting.
 */

const KEY = "kin_show_holidays";

/**
 * Default ON. An absent key means she has never touched the toggle, which is
 * not the same as having turned it off — so absence must read as the default,
 * and the default is on.
 */
export const SHOW_HOLIDAYS_DEFAULT = true;

export function readShowHolidays(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return SHOW_HOLIDAYS_DEFAULT;
    // Only the explicit off-value turns it off. Anything else — a stale value,
    // a hand-edited key, a half-written string — falls back to the default
    // rather than silently hiding the layer.
    return raw !== "0";
  } catch {
    return SHOW_HOLIDAYS_DEFAULT;
  }
}

export function writeShowHolidays(next: boolean): void {
  try {
    localStorage.setItem(KEY, next ? "1" : "0");
  } catch {
    // A device that cannot persist the choice still honours it for this
    // session — the cached value below is what the views read.
  }
  cached = next;
  for (const fn of listeners) fn();
}

/**
 * ── A subscribable store, because Settings and the Today tab are mounted at
 * the same time ──────────────────────────────────────────────────────────────
 *
 * `Index.tsx` is the whole app shell and all five tabs live inside it, so
 * toggling this in Settings and switching to Today does NOT remount the
 * calendar. A plain `useState(readShowHolidays)` would therefore keep showing
 * holidays until a full reload — the toggle would look broken.
 *
 * `useSyncExternalStore` wants a snapshot that is referentially stable between
 * changes, which is why the value is cached rather than re-read from
 * localStorage on every call: `getSnapshot` running on each render and
 * returning a fresh read would be fine for a boolean, but caching also means
 * the try/catch cost is paid once.
 */
let cached: boolean | null = null;
const listeners = new Set<() => void>();

export function subscribeShowHolidays(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function getShowHolidaysSnapshot(): boolean {
  if (cached === null) cached = readShowHolidays();
  return cached;
}
